<template>
  <div class="cart__body">
    <div class="cart__head">
      <div class="cart__title-wrapper">
        <h2 class="cart__title">Cart</h2>
        <span v-if="totalCartItems > 0" :key="totalCartItems" class="cart__count" id="cart-title" aria-live="polite">({{ totalCartItems }} items)</span>
      </div>
      <button v-if="!isCart" class="cart__close" aria-label="Close" @click="emit('on-toggle', false)">
        <svg class="cart__close-icon">
          <use href="#close"></use>
        </svg>
      </button>
    </div>
    <!-- <div v-if="!isEmpty" class="cart__freeShippingBar">
      <span v-if="freeLimitAchive != 100 && freeLimitCount" class="cart__discountCode">You’re ${{freeLimitCount}} away from getting free shipping.</span>
      <span v-if="cartProps.discountCodeMsg && freeLimitAchive === 100" class="cart__discountCode">{{cartProps.discountCodeMsg}}</span>
      <div v-if="cartProps.freeShippingLimit" class="cart__shippingBar-wrapper">
        <span class="cart__shippingBar-amount">$0</span>
        <div class="cart__shippingBar" :style="{ '--ship-progress': `${freeLimitAchive}%`}"></div>
        <span class="cart__shippingBar-amount">${{cartProps.freeShippingLimit}}</span>
      </div>
      <span v-if="cartProps.freeShippingMsg && freeLimitAchive === 100" class="cart__freeShippingMsg" v-html="cartProps.freeShippingMsg"></span>
    </div> -->
    <form v-if="!isEmpty" class="cart__form" action="/cart" method="post">
      <div v-if="cartConfirmationObj.itemRemoved" class="cartItem--removed">{{ cartConfirmationObj.message }}</div>

        <CartItem v-if="addingProduct" :placeholder="true" />
        <CartItem
          v-for="(item, index) in items"
          :cart="items"
          :key="item.variant_id"
          :item="item"
          :cartConfirmationObj="
            item.line === cartConfirmationObj.line && !addingProduct ? cartConfirmationObj : { visiblity: false }
          "
          @quantity-change="change($event)"
        />
        <template v-if="shouldShowUpsellItem && Object.keys(upsellConfig).length">
          <CartUpsell :data="upsellConfig.item" />
        </template>

      <div v-if="showDailyKarmaWidget && items && items.length" class="dk-flat-static"></div>
      <div v-if="items && items.length" class="cart__bottom">
        <div class="cart__calc-block">
          <div class="nvd-mini w-nvd-100 cart__parcel-protection"></div>
          <CartDiscount />

          <template v-if="hasCartLevelDiscounts">
            <div class="cart__calc-row">
              <span class="cart__calc-row-label">Subtotal</span>
              <span class="cart__calc-row-value">{{ itemsSubtotalFormatted }}</span>
            </div>
            <div
              v-for="(discount, index) in cartLevelDiscounts"
              :key="`${discount.title}-${index}`"
              class="cart__calc-row cart__calc-row--discount"
            >
              <span class="cart__calc-row-label">{{ discount.title }}</span>
              <span class="cart__calc-row-value">-{{ formatMoney(discount.total_allocated_amount) }}</span>
            </div>
          </template>

          <div v-if="subTotalFormatted" class="cart__calc">
            <span class="cart__calc-label">{{ hasCartLevelDiscounts ? 'Estimated total' : 'Subtotal' }}</span>
            <span class="cart__calc-value">{{ subTotalFormatted }}</span>
          </div>
        </div>

        <button
          :data-stio-gtm-id="isCart ? 'Cart: Checkout CTA' : 'Cart Drawer: Checkout CTA'"
          @click="checkingOut = true"
          name="checkout"
          class="btn btn--secondary btn--medium cart__checkout"
        >
          <span :class="{ 'cart__checkout-inner--processing': checkingOut }" class="cart__checkout-inner">
            CHECKOUT
          </span>
        </button>
        <CartExpressCheckout :isCartOpen="isCartOpen" />
        <div class="cart__highlight-text">{{ cartProps.cartHighLightMsg }}</div>
      </div>
    </form>
    <div v-else class="cart__empty">{{ cartProps.cartEmptyText }}</div>
  </div>
</template>

<script setup>
  import { ref, computed, onBeforeMount, watch, toRefs } from 'vue';
  import { useStore } from 'vuex';
  import cartHelpers from '../api';
  import CartItem from './cart-item.vue';
  import CartUpsell from './cart-upsell.vue';
  import CartExpressCheckout from './cart-express-checkout.vue';
  import CartDiscount from './cart-discount.vue';

  const { status } = cartHelpers;
  const store = useStore();
  var cbs = ref([]);
  var upsellConfig = ref({});
  var checkingOut = ref(false);

  const props = defineProps({
    isCart: {
      type: Boolean,
      default: false,
    },

    isCartOpen: {
      type: Boolean,
      default: false,
    },

    cartConfirmationObj: {
      type: Object,
      default: {
        visiblity: false,
        message: 'Item Added To Cart',
      },
    },

    showDailyKarmaWidget: {
      type: Boolean,
      default: false,
    },
  });

  onBeforeMount(async () => {
    // Setting window function for additem
    window.Additem = addItem;
    window.updateCartItems = update;
    window.changeThenAdd = changeThenAdd;
    window.cartItems = items.value;

    // Initialise store init function
    initCart();

    // upsell product function
    getUpsell();

    // daily karma refresh
    window.flatRefreshCartCallback = () => {
      initCart();
    };
  });

  const emit = defineEmits(['on-toggle', 'change-cartConfirmationObj']);

  const cart = computed(() => store.state.cart);
  const totalCount = computed(() => store.state.cart.item_count);
  const cartStatus = computed(() => store.state.status);
  const isEmpty = computed(() => store.state.status === status.empty);

  const cartProps = computed(() => {
    return window.cartProps;
  });

  const items = computed(() => {
    const items = cart.value.items || [];
    return items.map((item, index) => ({
      ...item,
      image: item.featured_image,
      line: index + 1,
    }));
  });

  const shouldShowUpsellItem = computed(() => {
    return (
      cartProps.value.upsellEnabled === true &&
      cart.value.items &&
      cart.value.items.length &&
      !cart.value.items.some(({ handle }) => handle === cartProps.value.upsellProductHandle)
    );
  });

  const totalCartItems = computed(() => {
    return totalCount.value;
  });

  const addingProduct = computed(() => {
    return cartStatus.value === status.adding;
  });

  const subTotal = computed(() => {
    return cart.value.total_price / 100;
  });

  const subTotalFormatted = computed(() => {
    return window.formattedPrice(cart.value.total_price / 100);
  });

  const cartLevelDiscounts = computed(() => store.getters.cartLevelDiscounts);

  const hasCartLevelDiscounts = computed(() => cartLevelDiscounts.value.length > 0);

  const itemsSubtotalFormatted = computed(() => {
    const amount = cart.value.items_subtotal_price ?? cart.value.original_total_price ?? cart.value.total_price;
    return window.formattedPrice(amount / 100);
  });

  const formatMoney = (cents = 0) => window.formattedPrice(cents / 100);

  const freeLimitAchive = computed(() => {
    const completed = (subTotal.value / Number(cartProps.value.freeShippingLimit)) * 100;
    return Math.min(100, completed);
  });

  const freeLimitCount = computed(() => {
    const available = Number(cartProps.value.freeShippingLimit) - subTotal.value;
    const value = Math.max(0, available);
    return value % 1 != 0 ? value.toFixed(2) : value;
  });

  const getUpsell = async () => {
    await fetch(`/products/${cartProps.value.upsellProductHandle}?view=json`)
      .then((resp) => resp.json())
      .then((data) => {
        upsellConfig.value.product = data;
        upsellConfig.value.variant = upsellConfig.value.product.variants.find(
          ({ id: variantId }) => Number(variantId) === Number(cartProps.value.upsellVariantId)
        );
        const { product, variant } = upsellConfig.value;
        upsellConfig.value.item = {
          message: cartProps.value.upsellMsg,
          image: variant.featured_image,
          title: product.title,
          variantTitle: variant.title,
          compareAtPrice: product.compare_at_price / 100,
          price: variant.price / 100,
          variantId: Number(cartProps.value.upsellVariantId),
          available: variant.available,
          handle: product.handle,
          product: product,
        };
      })
      .catch((error) => {
        console.error('Error', error);
      });
  };

  const initCart = () => {
    store.dispatch('init');
  };

  const getMessageVisibility = (messageObj = {}) => (
    messageObj.visiblity ?? messageObj.visibility ?? true
  );

  const buildAddToCartOptions = (messageObj = {}, trackingPayload = null) => ({
    messageObj,
    trackingPayload,
  });

  const addItem = (data, options = { messageObj: { visiblity: true }, trackingPayload: null }) => {
    const isStructuredOptions = options && (
      Object.prototype.hasOwnProperty.call(options, 'messageObj') ||
      Object.prototype.hasOwnProperty.call(options, 'trackingPayload')
    );
    const normalizedOptions = isStructuredOptions
      ? {
        messageObj: options.messageObj || { visiblity: true },
        trackingPayload: options.trackingPayload || null,
      }
      : buildAddToCartOptions(options, null);
    const { messageObj, trackingPayload } = normalizedOptions;
    const itemAddedToCart = (items.value || []).find(
      (item) =>
        item.variant_id === Number(data.id) && JSON.stringify(item.properties) === JSON.stringify(data.properties)
    );
    if (!itemAddedToCart) {
      return store.dispatch('add', { item: data, trackingPayload }).then(() => {
        emit('change-cartConfirmationObj', {
          visiblity: getMessageVisibility(messageObj),
          message: 'Item Added To Cart',
          line: 1,
        });
      });
    } else {
      return change({
        line: itemAddedToCart.line,
        quantity: itemAddedToCart.quantity + 1,
        trackingPayload,
      }).then(() => {
        emit('change-cartConfirmationObj', {
          visiblity: getMessageVisibility(messageObj),
          message: 'Item Updated',
          line: itemAddedToCart.line,
        });
      });
    }
  };

  const change = (data) => {
    const changeRequest = store.dispatch('change', data);

    if (data.quantity === 0) {
      emit('change-cartConfirmationObj', {
        message: 'Your item was removed!',
        itemRemoved: true,
      });
    }

    return changeRequest;
  };

  const update = (data) => {
    emit('change-cartConfirmationObj', {
      visiblity: false,
    });

    store.dispatch('update', data).then(() => {
      emit('change-cartConfirmationObj', {
        visiblity: true,
        message: 'Item Updated',
        line: 1,
      });
    });
  };

  const changeThenAdd = (data) => {
    emit('change-cartConfirmationObj', {
      visiblity: false,
    });

    store.dispatch('changeThenAdd', data).then(() => {
      emit('change-cartConfirmationObj', {
        visiblity: true,
        message: 'Item Updated',
        line: 1,
      });
    });
  };

  const clear = () => {
    store.dispatch('clear');
  };

  watch(cartStatus, (value) => {
    cbs.value.forEach((cb, index) => {
      cb(value);
      cbs.value.splice(index, 1);
    });
  });

  watch(totalCount, (count) => {
    const cartEl = document.querySelectorAll('.js-cart-count');
    cartEl.forEach((ele) => {
      const el = ele;
      el.dataset.cart = count;
      el.setAttribute('aria-label', `Cart with ${count} items`)
    });
  });

  watch(items, () => {
    window.cartItems = items.value;
    window.dispatchEvent(new Event('cartItemsUpdated'));
  });
</script>
